import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DocumentNodeModel } from '../../core/models/data/document.model';
import { DocumentService } from '../../core/services/document.service';
import { BootstrapService } from '../../core/services/bootstrap.service';
import { DocumentUtilities } from '../../core/utilities/document.utilities';
import { DocumentListSortOptions } from '../../core/enums/sort-options.enums';
import { DocumentPermission, DocumentStatus, FileTypeCategory } from '../../core/enums/data.enums';
import { ModalActions } from '../../core/enums/base.enums';
import { EscCloseDirective } from '../../core/directives/esc-close.directive';
import { FolderTreePickerComponent, FolderTreeSelection } from '../folder-tree-picker/folder-tree-picker.component';
import { DocumentsConstants } from '../../core/constants/documents.constants';

/**
 * Reusable modal for selecting a single existing file from the Document Center. Navigates folders
 * (via the folder-tree-picker) filtered to `permissions` (Public by default), lists the chosen folder's
 * files (optionally narrowed to `fileTypes`), and emits the selected node. Pending/Quarantined files
 * are shown but not selectable — only a scan-cleared (Available) file can be chosen.
 *
 * Deliberately presentation-only about the SELECTION: it emits the raw DocumentNodeModel and lets each
 * consumer resolve what it needs. The email composer attaches the node's `documentNodeId`; a WYSIWYG
 * image picker calls DocumentService.getDownloadUrl() on it to embed the file (a Public file resolves to
 * a CDN url). Give each instance a distinct `modalId` so several pickers can coexist on one page.
 */
@Component({
    selector: 'ms-document-file-picker',
    standalone: true,
    imports: [FontAwesomeModule, EscCloseDirective, FolderTreePickerComponent],
    templateUrl: './document-file-picker.component.html',
    styleUrl: './document-file-picker.component.scss'
})
export class DocumentFilePickerComponent {

    // Distinct DOM id per instance so multiple pickers (e.g. attachments + image) can coexist on a page.
    public modalId = input<string>('documentFilePickerModal');
    public title = input<string>('Select a file');
    public helpText = input<string>('Choose a folder, then select a file.');
    public confirmLabel = input<string>('Select');
    // Which folders are browsable (files inherit their folder's permission). Undefined = all folders
    // (email attachments may come from any folder); pass [Public] to restrict — e.g. images embedded in
    // an email body, which every recipient must be able to load.
    public permissions = input<DocumentPermission[] | undefined>(undefined);
    // Optionally narrow the listed files to certain categories (e.g. [FileTypeCategory.Image]); undefined = all.
    public fileTypes = input<FileTypeCategory[] | undefined>(undefined);
    // 'list' = compact rows (attachment picker); 'grid' = thumbnail cards that preview image files
    // (used by the WYSIWYG image picker). Grid resolves a CDN url per image for the <img> preview.
    public layout = input<'list' | 'grid'>('list');

    public fileSelected = output<DocumentNodeModel>();
    public cancelled = output<void>();

    private documentService = inject(DocumentService);
    private bootstrapService = inject(BootstrapService);

    // Seed level of the tree: the org's browsable root folders. Reloaded on each open so new folders appear.
    public rootFolders = signal<DocumentNodeModel[]>([]);
    public selectedFolder = signal<FolderTreeSelection>(undefined);
    public files = signal<DocumentNodeModel[]>([]);
    public selectedFile = signal<DocumentNodeModel | undefined>(undefined);

    public isLoadingFolders = signal<boolean>(false);
    public isLoadingFiles = signal<boolean>(false);
    // True when a folder holds more files than the single page we load — surfaced so the cap is never silent.
    public hasMoreFiles = signal<boolean>(false);
    // Resolved CDN preview urls for image files (grid layout only), keyed by documentNodeId.
    public previewUrls = signal<Map<number, string>>(new Map());

    public DocumentStatus = DocumentStatus;

    // Only the number branch is a real folder; the tree never offers the Root here (files live in folders).
    public hasFolderSelected = computed<boolean>(() => typeof this.selectedFolder() === 'number');
    public canConfirm = computed<boolean>(() => this.isSelectable(this.selectedFile()));

    constructor() {
        // Load the picked folder's files whenever the tree selection changes; clear the pending file pick.
        effect(() => {
            const selection = this.selectedFolder();
            this.selectedFile.set(undefined);
            if (typeof selection === 'number') {
                void this.loadFiles(selection);
            } else {
                this.files.set([]);
                this.hasMoreFiles.set(false);
            }
        });
    }

    // Lazily fetch a folder's browsable child folders as the tree expands.
    public loadChildren = (folderId: number): Promise<DocumentNodeModel[]> =>
        this.documentService.getChildren(folderId, DocumentListSortOptions.NameAsc, this.permissions())
            .then(result => (result.success ? result.data ?? [] : []));

    public async open(): Promise<void> {
        this.reset();
        this.isLoadingFolders.set(true);
        const result = await this.documentService.getChildren(undefined, DocumentListSortOptions.NameAsc, this.permissions());
        this.isLoadingFolders.set(false);
        this.rootFolders.set(result.success ? result.data ?? [] : []);
        this.bootstrapService.manageModal(`#${this.modalId()}`, ModalActions.Show);
    }

    public selectFile(file: DocumentNodeModel): void {
        if (!this.isSelectable(file)) {
            return;
        }
        this.selectedFile.set(file);
    }

    public isSelected(file: DocumentNodeModel): boolean {
        return this.selectedFile()?.documentNodeId === file.documentNodeId;
    }

    // A file can be chosen only once its scan has cleared. Absent status = legacy/available.
    public isSelectable(file: DocumentNodeModel | undefined): boolean {
        return !!file && file.status !== DocumentStatus.Pending && file.status !== DocumentStatus.Quarantined;
    }

    public formatBytes(bytes: number | undefined): string {
        return this.documentService.formatBytes(bytes);
    }

    public thumbnailUrl(file: DocumentNodeModel): string | undefined {
        return this.previewUrls().get(file.documentNodeId);
    }

    public fileIcon(file: DocumentNodeModel): string {
        return DocumentUtilities.fileTypeIcon(file.fileTypeCategory);
    }

    public confirm(): void {
        const file = this.selectedFile();
        if (!this.isSelectable(file)) {
            return;
        }
        this.fileSelected.emit(file!);
        this.close();
    }

    public cancel(): void {
        this.cancelled.emit();
        this.close();
    }

    public close(): void {
        this.bootstrapService.manageModal(`#${this.modalId()}`, ModalActions.Hide);
    }

    private async loadFiles(folderId: number): Promise<void> {
        this.isLoadingFiles.set(true);
        this.previewUrls.set(new Map());
        const result = await this.documentService.getFiles(
            { folderId, permissions: this.permissions(), fileTypes: this.fileTypes() },
            DocumentListSortOptions.NameAsc, 0, DocumentsConstants.FILE_PICKER_PAGE_SIZE()
        );
        this.isLoadingFiles.set(false);

        const files = result.success ? result.data?.data ?? [] : [];
        this.files.set(files);
        this.hasMoreFiles.set(result.data?.hasNext ?? false);

        if (this.layout() === 'grid') {
            void this.resolveThumbnails(files, folderId);
        }
    }

    // Resolve a CDN url per image file for the grid preview — a Public file's download url doubles as an
    // <img src>. Ignores results if the user navigated to another folder while a request was in flight.
    private async resolveThumbnails(files: DocumentNodeModel[], folderId: number): Promise<void> {
        const images = files.filter(file => DocumentUtilities.isImageFile(file) && this.isSelectable(file));
        await Promise.all(images.map(async file => {
            const result = await this.documentService.getDownloadUrl(file.documentNodeId);
            if (this.selectedFolder() !== folderId) {
                return;
            }
            if (result.success && result.data?.downloadUrl) {
                this.previewUrls.update(map => new Map(map).set(file.documentNodeId, result.data!.downloadUrl));
            }
        }));
    }

    private reset(): void {
        this.selectedFolder.set(undefined);
        this.selectedFile.set(undefined);
        this.files.set([]);
        this.hasMoreFiles.set(false);
        this.previewUrls.set(new Map());
    }
}
